import {state} from '@bitstillery/mithril'
import {MithrilComponent} from '@bitstillery/mithril'
import {classes, property_of} from '@bitstillery/common/lib/utils'
import m from '@bitstillery/mithril'
import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

export const dropdownMeta = {
    tier: 'inline',
    summary: 'Trigger that reveals a floating menu of links or actions.',
    when_to_use:
        'A compact set of links that would crowd the page laid out flat (a language or region picker, a grouped set of shortcuts). The first child is the trigger — a `Button` with `variant="menu"` or an `Icon`; everything after it is the menu, and Dropdown floats it in its own surface. Menu entries are `Button variant="menu"`, one per line. Clicking an entry closes the menu; `closeOnSelect={false}` keeps it open. Not for page navigation chrome — the portal header and breadcrumbs own that.',
    props: 'hover?: boolean — Open while the pointer rests on the trigger (desktop). Touch devices tap the trigger either way.\ndisabled?: boolean — Trigger does nothing and the menu never opens.\ncloseOnSelect?: boolean (default true) — Set false for a menu that stays open across clicks (multi-select, filters).',
    example: `
    <Dropdown hover>
        <Button icon="globe" text="Regions" type="info" variant="menu" />
        <Button link="/regions/europe" text="Europe" variant="menu" />
        <Button link="/regions/asia" text="Asia" variant="menu" />
    </Dropdown>
    `,
} satisfies CmsComponentMeta

export interface DropdownAttrs {
    className?: string
    key?: string | undefined
    model?:
        | {
              toggle: boolean
          }
        | undefined
    /** Tested for truthiness only. */
    disabled?: boolean | number | null
    /** Open the menu while the pointer hovers the trigger (desktop). Touch devices, where hover
     *  doesn't fire, keep using an explicit tap on the trigger to toggle. */
    hover?: boolean
    /** A click on a menu item closes the menu by default. Set `false` for form-style bodies
     *  that stay open across interactions (multi-select checkbox lists, filter panels). */
    closeOnSelect?: boolean
}

/** A child that already declares `.dropdown-body` is the caller's own menu element — often with extra
 *  classes on it — and is rendered untouched. */
function is_body_element(child: m.Children): boolean {
    if (typeof child !== 'object' || child === null || Array.isArray(child)) {
        return false
    }
    const class_names = [property_of(child.attrs, 'className'), property_of(child.attrs, 'class')]
    return class_names.some((class_name) => typeof class_name === 'string' && class_name.includes('dropdown-body'))
}

export class Dropdown extends MithrilComponent<DropdownAttrs> {
    data = state({toggle: false})

    remove_outside_listener: () => void = () => {}

    remove_select_listener: () => void = () => {}

    model(vnode: m.Vnode<DropdownAttrs>) {
        return vnode.attrs.model ?? this.data
    }

    /** Everything after the trigger is the menu. Callers that supply their own `.dropdown-body`
     *  element keep it; anything else we float ourselves — the only shape a CMS view can author,
     *  since those compose from catalog components and cannot write the element. */
    body(vnode: m.Vnode<DropdownAttrs>): m.Children {
        const children = Array.isArray(vnode.children) ? vnode.children : [vnode.children]
        const menu = children.slice(1).filter((child) => child !== null && child !== undefined && typeof child !== 'boolean')
        if (!menu.length) {
            return null
        }
        if (menu.length === 1 && is_body_element(menu[0])) {
            return menu[0]
        }
        return <div className='dropdown-body'>{menu}</div>
    }

    override oncreate(vnode: m.VnodeDOM<DropdownAttrs>) {
        const close_on_outside_click = (event: MouseEvent) => {
            const model = this.model(vnode)
            if (model.toggle && !(event.target instanceof Node && vnode.dom.contains(event.target))) {
                model.toggle = false
                m.redraw()
            }
        }
        document.addEventListener('click', close_on_outside_click)
        this.remove_outside_listener = () => document.removeEventListener('click', close_on_outside_click)

        // Close-on-select runs in the capture phase so it fires before the clicked item's own
        // handler. Menu items that navigate (e.g. a `Button` with a `link`) call
        // `event.stopPropagation()`, which would prevent a bubbling handler from ever seeing the
        // click and leave the menu stuck open; capturing sidesteps that.
        const close_on_select = (event: Event) => {
            const model = this.model(vnode)
            if (!model.toggle || vnode.attrs.closeOnSelect === false) {
                return
            }
            if (event.target instanceof Element && event.target.closest('.dropdown-body')) {
                model.toggle = false
                m.redraw()
            }
        }
        vnode.dom.addEventListener('click', close_on_select, true)
        this.remove_select_listener = () => vnode.dom.removeEventListener('click', close_on_select, true)
    }

    override onremove() {
        this.remove_outside_listener()
        this.remove_select_listener()
    }

    view(vnode: m.Vnode<DropdownAttrs>) {
        const model = this.model(vnode)
        return (
            <div
                className={classes('c-dropdown', vnode.attrs.className, {
                    active: !vnode.attrs.disabled && model.toggle,
                    disabled: vnode.attrs.disabled,
                })}
                // A tap or click on the trigger toggles the menu; this works the same on
                // touch and pointer devices. A click inside the open menu is a selection: the
                // capture-phase listener (see oncreate) closes the menu by default — form-style
                // bodies opt out with `closeOnSelect={false}` (see attrs) to stay open across
                // interactions. Either way a body click must not fall through to the toggle below.
                onclick={(event: MouseEvent) => {
                    if (vnode.attrs.disabled) {
                        return
                    }
                    if (event.target instanceof Element && event.target.closest('.dropdown-body')) {
                        return
                    }
                    model.toggle = !model.toggle
                }}
                // Hover-to-open for pointer devices (opt-in). These handlers don't fire on touch,
                // where the trigger's own tap handling keeps working.
                onmouseenter={
                    vnode.attrs.hover && !vnode.attrs.disabled
                        ? () => {
                              model.toggle = true
                          }
                        : undefined
                }
                onmouseleave={
                    vnode.attrs.hover
                        ? () => {
                              model.toggle = false
                          }
                        : undefined
                }
                key={vnode.attrs.key}
            >
                {(Array.isArray(vnode.children) ? vnode.children : [vnode.children])[0]}
                {this.body(vnode)}
            </div>
        )
    }
}
